/**
 * Transfer-function editor maths (TransferFunctionHistogram.cs, HistogramBinning.cs,
 * HistogramHuAxis.cs, TransferFunctionDomain.cs): density histograms on 256 bins with the
 * desktop's log scale, the HU axis ticks, the shift/stretch domain mapping between the
 * authored ("base") TF axis and the effective axis the volume is rendered with, and the
 * "rendered opacity" curve that accounts for the step-corrected alpha and the box opacity.
 */
import type { TfDomain, TransferFunctionJson } from './tf';

export const HISTOGRAM_BINS = 256;
export const MIN_POINT_SEPARATION = 1 / 1023;
export const DOMAIN_SCALE_MIN = 0.05;
export const DOMAIN_SCALE_MAX = 20;

/** Raw bin counts of an 8-bit volume. `include(index)` restricts to a region. */
export function histogramCounts(bytes: Uint8Array, channels: 1 | 4, include?: (voxel: number) => boolean): Float64Array {
  const counts = new Float64Array(HISTOGRAM_BINS);
  const n = channels === 1 ? bytes.length : bytes.length / 4;
  if (include) {
    for (let v = 0; v < n; v++) if (include(v)) counts[bytes[v * channels]]++;
  } else {
    for (let v = 0; v < n; v++) counts[bytes[v * channels]]++;
  }
  return counts;
}

/** HistogramBinning log scale: h = log10(1 + f) / log10(1 + max). */
export function normalizeLog(counts: ArrayLike<number>): Float32Array {
  let max = 0;
  for (let i = 0; i < counts.length; i++) if (counts[i] > max) max = counts[i];
  const out = new Float32Array(counts.length);
  if (max <= 0) return out;
  const denom = Math.log10(1 + max);
  for (let i = 0; i < counts.length; i++) out[i] = counts[i] > 0 ? Math.log10(1 + counts[i]) / denom : 0;
  return out;
}

/** HistogramHuAxis: the first step that keeps labels at least 60 px apart. */
export function huTickStep(rangeHu: number, widthPx: number, minLabelPx = 60): number {
  const steps = [100, 200, 250, 500, 1000, 2000, 4000];
  for (const s of steps) {
    const px = (s / Math.max(1, rangeHu)) * widthPx;
    if (px >= minLabelPx) return s;
  }
  return steps[steps.length - 1];
}

/** Tick HU values inside [min, max]: multiples of the step plus both range ends. */
export function huTicks(minHu: number, maxHu: number, widthPx: number, minLabelPx = 60): number[] {
  const range = Math.max(1, maxHu - minHu);
  const step = huTickStep(range, widthPx, minLabelPx);
  const out = [minHu];
  const first = Math.ceil(minHu / step) * step;
  // a regular tick too close to either end label would overprint it
  const guard = (minLabelPx / 2 / widthPx) * range;
  for (let v = first; v < maxHu; v += step) if (v - minHu > guard && maxHu - v > guard) out.push(v);
  out.push(maxHu);
  return out;
}

// ------------------------------------------------------------- domain (shift / stretch)

export function clampScale(scale: number): number {
  return Math.min(DOMAIN_SCALE_MAX, Math.max(DOMAIN_SCALE_MIN, scale));
}

/** Base (authored) axis -> effective axis: pivot + scale * (b - pivot) + shift. */
export function toEffective(b: number, d: TfDomain): number {
  return d.pivot + clampScale(d.scale) * (b - d.pivot) + d.shift;
}

/** Effective axis -> base axis: pivot + (e - shift - pivot) / scale. */
export function toBase(e: number, d: TfDomain): number {
  return d.pivot + (e - d.shift - d.pivot) / clampScale(d.scale);
}

export function isNeutral(d: TfDomain): boolean {
  return d.shift === 0 && d.scale === 1;
}

/** The effective-axis interval [toEffective(0), toEffective(1)]; outside it the texture reads
 * the base curve's end values (the "saturated" hatched bands). */
export function unsaturatedRange(d: TfDomain): [number, number] {
  const a = toEffective(0, d);
  const b = toEffective(1, d);
  return a <= b ? [a, b] : [b, a];
}

/** Shift in normalised density units for a shift in HU. */
export function shiftHuToNormalized(hu: number, huRange: [number, number]): number {
  return hu / Math.max(1, huRange[1] - huRange[0]);
}

/** Step the value by 1/1023 away from any coincident point. */
export function nudgeAwayFromCoincident(value: number, others: number[], step = MIN_POINT_SEPARATION): number {
  let v = value;
  let guard = 0;
  const coincident = (x: number) => others.some((o) => Math.abs(o - x) < step * 0.5);
  let dir = 1;
  while (coincident(v) && guard++ < 2048) {
    v += step * dir;
    if (v > 1 + 1e-9) {
      v = value - step;
      dir = -1;
    }
  }
  return v;
}

/** The opacity a sample actually contributes after the step correction and the box opacity:
 * (1 - (1 - a)^(1/m)) * boxOpacity. */
export function renderedOpacity(alpha: number, samplingMultiplier: number, boxOpacity: number): number {
  const m = Math.max(0.05, samplingMultiplier);
  return (1 - Math.pow(1 - Math.min(1, Math.max(0, alpha)), 1 / m)) * boxOpacity;
}

/** The editor shows the dotted rendered-opacity curve in DVR when it differs from the alpha curve. */
export function showsRenderedOpacity(samplingMultiplier: number, boxOpacity: number): boolean {
  return Math.abs(samplingMultiplier - 1) > 1e-6 || boxOpacity < 0.999;
}

// ------------------------------------------------------------- authored-curve sampling

function smoothStep(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
}

/** Alpha of the authored curve at a base-axis value, with the bake's injected endpoints. */
export function sampleAlpha(tf: TransferFunctionJson, u: number): number {
  const pts = tf.alphaPoints.map((p) => ({ v: p.dataValue, a: p.alphaValue })).sort((a, b) => a.v - b.v);
  if (pts.length === 0 || pts[pts.length - 1].v < 1) pts.push({ v: 1, a: 1 });
  if (pts[0].v > 0) pts.unshift({ v: 0, a: 0 });
  return interpolate(pts, u, (p) => p.a);
}

/** sRGB colour of the authored curve at a base-axis value. */
export function sampleColour(tf: TransferFunctionJson, u: number): [number, number, number] {
  const pts = tf.colourPoints.map((p) => ({ v: p.dataValue, c: p.colourValue })).sort((a, b) => a.v - b.v);
  if (pts.length === 0 || pts[pts.length - 1].v < 1) pts.push({ v: 1, c: { r: 1, g: 1, b: 1 } });
  if (pts[0].v > 0) pts.unshift({ v: 0, c: { r: 1, g: 1, b: 1 } });
  return [interpolate(pts, u, (p) => p.c.r), interpolate(pts, u, (p) => p.c.g), interpolate(pts, u, (p) => p.c.b)];
}

function interpolate<T extends { v: number }>(sorted: T[], u: number, pick: (p: T) => number): number {
  if (sorted.length === 1) return pick(sorted[0]);
  let i = 0;
  while (i < sorted.length - 2 && sorted[i + 1].v < u) i++;
  const l = sorted[i];
  const r = sorted[i + 1];
  const span = r.v - l.v;
  const f = span > 1e-6 ? smoothStep((Math.min(Math.max(u, l.v), r.v) - l.v) / span) : 1;
  return pick(r) * f + pick(l) * (1 - f);
}

/** Immutable point edits: every call returns a new TF object with new arrays. */
export const tfEdit = {
  clone(tf: TransferFunctionJson): TransferFunctionJson {
    return {
      version: tf.version ?? 1,
      colourPoints: tf.colourPoints.map((p) => ({ dataValue: p.dataValue, colourValue: { ...p.colourValue } })),
      alphaPoints: tf.alphaPoints.map((p) => ({ dataValue: p.dataValue, alphaValue: p.alphaValue })),
    };
  },
  addAlpha(tf: TransferFunctionJson, dataValue: number, alphaValue: number): { tf: TransferFunctionJson; index: number } {
    const out = tfEdit.clone(tf);
    const v = nudgeAwayFromCoincident(dataValue, out.alphaPoints.map((p) => p.dataValue));
    out.alphaPoints.push({ dataValue: v, alphaValue: Math.min(1, Math.max(0, alphaValue)) });
    return { tf: out, index: out.alphaPoints.length - 1 };
  },
  moveAlpha(tf: TransferFunctionJson, index: number, dataValue: number, alphaValue: number): TransferFunctionJson {
    const out = tfEdit.clone(tf);
    const others = out.alphaPoints.filter((_, i) => i !== index).map((p) => p.dataValue);
    out.alphaPoints[index] = { dataValue: nudgeAwayFromCoincident(dataValue, others), alphaValue: Math.min(1, Math.max(0, alphaValue)) };
    return out;
  },
  removeAlpha(tf: TransferFunctionJson, index: number): TransferFunctionJson {
    const out = tfEdit.clone(tf);
    out.alphaPoints.splice(index, 1);
    return out;
  },
  addColour(tf: TransferFunctionJson, dataValue: number, rgb: [number, number, number]): { tf: TransferFunctionJson; index: number } {
    const out = tfEdit.clone(tf);
    const v = nudgeAwayFromCoincident(dataValue, out.colourPoints.map((p) => p.dataValue));
    out.colourPoints.push({ dataValue: v, colourValue: { r: rgb[0], g: rgb[1], b: rgb[2], a: 1 } });
    return { tf: out, index: out.colourPoints.length - 1 };
  },
  moveColour(tf: TransferFunctionJson, index: number, dataValue: number): TransferFunctionJson {
    const out = tfEdit.clone(tf);
    const others = out.colourPoints.filter((_, i) => i !== index).map((p) => p.dataValue);
    out.colourPoints[index] = { ...out.colourPoints[index], dataValue: nudgeAwayFromCoincident(dataValue, others) };
    return out;
  },
  recolour(tf: TransferFunctionJson, index: number, rgb: [number, number, number]): TransferFunctionJson {
    const out = tfEdit.clone(tf);
    out.colourPoints[index] = { ...out.colourPoints[index], colourValue: { r: rgb[0], g: rgb[1], b: rgb[2], a: 1 } };
    return out;
  },
  removeColour(tf: TransferFunctionJson, index: number): TransferFunctionJson {
    const out = tfEdit.clone(tf);
    out.colourPoints.splice(index, 1);
    return out;
  },
};
